import React, { useEffect, useMemo, useState } from "react"
import { Checkbox, Form, InputNumber, Modal, message } from "antd"
import { organizationApi } from "../../Apis/organizationApi"
import {
  parseOrganizationApiError,
  unwrapOrganizationResult,
  buildPlanFeatureOptions,
  filterMemberFeaturesToPlan,
  getOrganizationMemberFurnitureFinderStats,
} from "../../utils/organizationApiHelpers"
import { translations } from "../../utils/Translation/staticTranslation"
import { getAppLanguage } from "../../utils/getAppLanguage"

const AllocateOrganizationMemberModal = ({
  open,
  onClose,
  onSuccess,
  member,
  allowedPlanFeatures = [],
  maxImages = 0,
  maxFurnitureFinder = 0,
  showFurnitureFinder = false,
}) => {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const lang = getAppLanguage()
  const t = translations[lang]?.organizationPage?.allocateModal

  const featureOptions = useMemo(
    () => buildPlanFeatureOptions(allowedPlanFeatures, lang),
    [allowedPlanFeatures, lang],
  )

  const furnitureFinderStats = useMemo(
    () => getOrganizationMemberFurnitureFinderStats(member),
    [member],
  )

  useEffect(() => {
    if (open && member) {
      const memberFeatures = filterMemberFeaturesToPlan(
        member.allowed_features,
        allowedPlanFeatures,
      )
      form.setFieldsValue({
        images: 0,
        furniture_finder: 0,
        allowed_features: memberFeatures,
      })
    }
  }, [open, member, form, allowedPlanFeatures])

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const onFinish = async (values) => {
    if (!member?.id) return
    const images = Number(values.images) || 0
    const furnitureFinder = Number(values.furniture_finder) || 0
    const features = filterMemberFeaturesToPlan(
      values.allowed_features || [],
      allowedPlanFeatures,
    )
    if (images === 0 && furnitureFinder === 0 && features.length === 0) {
      message.warning(t?.nothingToUpdate)
      return
    }

    setSubmitting(true)
    try {
      const payload = { images }
      if (showFurnitureFinder && furnitureFinder > 0) {
        payload.furniture_finder = furnitureFinder
      }
      if (features.length > 0) {
        payload.allowed_features = features
      }
      const response = await organizationApi.allocateMember(member.id, payload)
      const result = unwrapOrganizationResult(response)
      message.success(t?.success)
      onSuccess?.(result)
      handleCancel()
    } catch (error) {
      message.error(parseOrganizationApiError(error, lang))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      className="org-modal"
      title={t?.title}
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <p className="mb-20 text-14 text-484747">
        {t?.subtitle} <strong>{member?.email}</strong>
      </p>
      {showFurnitureFinder && furnitureFinderStats.pending > 0 ? (
        <div className="mb-16">
          {furnitureFinderStats.pending > 0 ? (
            <p className="text-14 text-A1A1A1">
              {t?.furnitureFinderPendingNote?.replace(
                "{pending}",
                String(furnitureFinderStats.pending),
              )}
            </p>
          ) : null}
        </div>
      ) : null}
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="images"
          label={t?.images}
          extra={t?.imagesHint?.replace("{max}", String(maxImages))}
        >
          <InputNumber min={0} max={maxImages} className="w-100" />
        </Form.Item>
        {showFurnitureFinder ? (
          <Form.Item
            name="furniture_finder"
            label={t?.furnitureFinder}
            extra={t?.furnitureFinderHint?.replace(
              "{max}",
              String(maxFurnitureFinder),
            )}
          >
            <InputNumber min={0} max={maxFurnitureFinder} className="w-100" />
          </Form.Item>
        ) : null}
        {featureOptions.length > 0 ? (
          <Form.Item
            name="allowed_features"
            label={t?.features}
            extra={t?.featuresFromPlan}
          >
            <Checkbox.Group
              className="org-feature-checkbox-group"
              options={featureOptions}
            />
          </Form.Item>
        ) : (
          <p className="text-14 text-A1A1A1 mb-16">{t?.noPlanFeatures}</p>
        )}
        <button
          type="submit"
          className="add-store org-modal-submit"
          disabled={submitting}
        >
          {submitting ? t?.saving : t?.submit}
        </button>
      </Form>
    </Modal>
  )
}

export default AllocateOrganizationMemberModal
